import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Button } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import React, { useCallback, useMemo } from 'react';

import { Card, VendorCheckNotice, VendorLine } from '../../../components/_mui';
import { CURRENTLY_UNAVAILABLE_LABEL, OUT_OF_STOCK_LABEL, PICKED_FOR_YOU_TITLE } from '../../../constants/marketplace';
import { ICampaign } from '../../../types/pyg';
import { isCampaignBlocked } from '../../../util/helpers';
import { isMarketplaceId } from '../../../util/marketplace';

interface IProps {
  campaign: ICampaign;
  isPicked: boolean;
  // Campaign-level vendor check in flight that the sidebar is not already showing a skeleton for.
  isChecking: boolean;
  vendor?: string;
  buttonSx: SxProps<Theme>;
  onPick: (campaign: ICampaign) => void;
  onViewDetails: (campaign: ICampaign, skuIndex?: number) => void;
}

export const CampaignCard = ({ campaign, isPicked, isChecking, vendor, buttonSx, onPick, onViewDetails }: IProps) => {
  const isMarketplace = isMarketplaceId(campaign.id);
  const isUnavailable = isMarketplace && campaign.items[0]?.available === false;
  const isDisabled = isMarketplace ? isUnavailable : !campaign.is_digital && isCampaignBlocked(campaign);
  // A multi-item marketplace campaign draws from several shops, so no single vendor can head it;
  // the row stays reserved so Select sits on the same line across the grid.
  const isMarketplaceSet = isMarketplace && campaign.items.length > 1;
  const isPickedIconShown = isPicked && !isDisabled;

  const submitBtnText = useMemo(() => {
    if (isDisabled) {
      return isMarketplace ? CURRENTLY_UNAVAILABLE_LABEL : OUT_OF_STOCK_LABEL;
    }

    return isPicked ? 'Selected' : 'Select';
  }, [isDisabled, isMarketplace, isPicked]);

  const selectButtonIcon = useMemo(() => (isPickedIconShown ? <CheckCircleIcon /> : undefined), [isPickedIconShown]);

  const handlePick = useCallback(() => onPick(campaign), [onPick, campaign]);

  const handleSkuIndexClick = useCallback(
    (skuIndex: number) => onViewDetails(campaign, skuIndex),
    [onViewDetails, campaign],
  );

  return (
    <Card
      campaign={campaign}
      fallbackTitle={isMarketplace ? PICKED_FOR_YOU_TITLE : undefined}
      isDisabled={isDisabled}
      isSelected={isPicked && !isChecking}
      subtitle={<VendorLine vendor={isMarketplaceSet ? undefined : vendor} reserveSpace />}
      onSkuIndexClick={handleSkuIndexClick}
      actions={
        isChecking ? (
          <VendorCheckNotice hasBorder fullWidth sx={buttonSx} />
        ) : (
          <Button
            fullWidth
            variant={isPicked ? 'contained' : 'outlined'}
            color="primary"
            size="medium"
            sx={buttonSx}
            startIcon={selectButtonIcon}
            disabled={isDisabled}
            onClick={handlePick}
          >
            {submitBtnText}
          </Button>
        )
      }
    />
  );
};
